import { useRef } from "react";
import type { Announcements, ScreenReaderInstructions } from "@dnd-kit/core";

export type SortableAccessibility = {
  announcements: Announcements;
  screenReaderInstructions: ScreenReaderInstructions;
};

/**
 * What a screen reader hears while a row of a sortable list is dragged —
 * pass the result as `DndContext`'s `accessibility`.
 *
 * dnd-kit's default announcements name the raw sortable ids ("item-1 was
 * moved over item-0", or a rule's ULID); these say which row it is and where
 * it is going instead. A row is named by the position it was picked up from
 * ("action 2 of 3"), which stays the same name for the whole drag.
 */
export const useSortableAccessibility = (
  ids: readonly string[],
  itemLabel: string,
): SortableAccessibility => {
  // dnd-kit reports the row as over its own slot the moment it is picked
  // up; announcing that would talk over "Picked up". Once it has moved, a
  // return to the starting slot is a move like any other.
  const hasMoved = useRef(false);

  const count = ids.length;
  const named = (id: string | number) =>
    `${itemLabel} ${ids.indexOf(String(id)) + 1}`;
  const position = (id: string | number) =>
    `position ${ids.indexOf(String(id)) + 1} of ${count}`;

  return {
    announcements: {
      onDragStart: ({ active }) => {
        hasMoved.current = false;
        return `Picked up ${named(active.id)} of ${count}.`;
      },
      onDragOver: ({ active, over }) => {
        if (over?.id === active.id && !hasMoved.current) return undefined;
        hasMoved.current = true;
        return over
          ? `Moved ${named(active.id)} to ${position(over.id)}.`
          : `${named(active.id)} is no longer over a position.`;
      },
      onDragEnd: ({ active, over }) =>
        over
          ? `Dropped ${named(active.id)} at ${position(over.id)}.`
          : `Dropped ${named(active.id)}; the order is unchanged.`,
      onDragCancel: ({ active }) =>
        `Cancelled. ${named(active.id)} is back at ${position(active.id)}.`,
    },
    screenReaderInstructions: {
      draggable: `To reorder this ${itemLabel}, press space or enter to pick it up, use the up and down arrow keys to move it, then press space or enter again to drop it, or escape to cancel.`,
    },
  };
};
